import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Autocomplete,
  LinearProgress,
} from '@mui/material';
import { Dashboard } from '@mui/icons-material';
import moment from 'moment';
import { toast } from 'react-toastify';
//COMPONENTS
import PlanHeader from './components/PlanHeader';
import PlanItemsGrid from './components/PlanItemsGrid';
import NotFoundTemplate from '../../components/AppLayout/NotFoundTemplate';
//CONTEXT
import usePlanDetailsContext from './context/usePlanDetailsContext';

// API
import { postPlanConfirmation, getWarehousePrepers } from '../../api/productSamplingWorkflowApi';

export default function WarehouseView() {
  const { plan, type, loading, planNotFound } = useContext(usePlanDetailsContext);

  const CONFIRMABLE_STATUS_IDS = [2, 3];
  const [staff, setStaff] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);
  const [form, setForm] = useState({});

  const loadWarehouseStaffDropdown = async () => {
    const response = await getWarehousePrepers();
    const { code, data } = response.data;
    if (code === '200') {
      const dropdownValue = data.map((row) => ({ label: row.username, value: row.id }));
      setStaffOptions(dropdownValue);
    }
  };

  const handleSavePartial = async (updatedForm, id) => {
    if (staff === null) {
      toast.warning('Assigned to is required');
      return;
    }

    const request = {
      id: updatedForm.plan_id,
      confirmed_by: staff?.value,
      products: updatedForm.products
        .filter((row) => row.id === id)
        .map((row) => ({ product_id: row.id, confirmed_qty: row.confirmed_qty })),
    };

    const response = await postPlanConfirmation(request);
    const { code, message } = response.data;

    if (code === '00003') {
      toast.dismiss();
      toast.success(message);
    }
  };

  const handleConfirmedPlan = async () => {
    if (staff === null) {
      toast.warning('Assigned to is required');
      return;
    }

    toast.loading('Saving  confirmation please kindly wait...');
    const request = {
      id: form.plan_id,
      confirmed_by: staff?.value,
      confirmed_date: moment().format('YYYY-MM-DD'),
      products: form.products.map((row) => ({
        product_id: row.id,
        confirmed_qty: row.confirmed_qty,
      })),
    };
    const response = await postPlanConfirmation(request);
    const { code, message } = response.data;

    if (code === '00003') {
      toast.dismiss();
      toast.success(message);
      window.location.reload();
    }
  };

  useEffect(() => {
    setForm(plan);
    loadWarehouseStaffDropdown();
  }, [plan]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (planNotFound) {
    return <NotFoundTemplate active={false} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f2f2f2',
      }}
    >
      <Box
        sx={{
          height: 160,
          bgcolor: 'rgb(107, 141, 62)',
        }}
      />

      <Paper
        elevation={3}
        sx={{
          width: 750,
          maxWidth: '95%',
          mx: 'auto',
          mt: '-120px',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              component='img'
              src='https://erp-company-logo.s3.ap-southeast-1.amazonaws.com/bk_logo.png'
              alt='Logo'
              sx={{
                height: 60,
                objectFit: 'contain',
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Dashboard sx={{ color: '#65748B' }} />
              <Typography variant='h5' fontWeight={700}>
                Plan Items
              </Typography>
            </Box>
            {CONFIRMABLE_STATUS_IDS.includes(form.status_id) && (
              <Autocomplete
                disablePortal={false}
                id='assigned_to'
                disableClearable
                fullWidth
                options={staffOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='Assigned To' />}
                size='small'
                value={staff}
                onChange={(e, newValue) => setStaff(newValue)}
                sx={{ backgroundColor: '#fff', width: '40%' }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <PlanHeader type={type} form={form} setForm={setForm} />
          </Box>

          <Box
            sx={{
              mb: 5,
              width: '100%',
            }}
          >
            <PlanItemsGrid
              type={type}
              form={form}
              setForm={setForm}
              handleSavePartial={handleSavePartial}
              staff={staff}
            />
          </Box>
        </Box>

        {/* Footer */}
        {CONFIRMABLE_STATUS_IDS.includes(form.status_id) && (
          <Box
            sx={{
              borderTop: '1px solid #E5E7EB',
              p: 3,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant='contained'
              color='success'
              sx={{
                borderRadius: 20,
                px: 4,
                textTransform: 'none',
                boxShadow: 'none',
              }}
              onClick={() => handleConfirmedPlan()}
            >
              Submit
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
